import { FC, memo, useEffect, useState } from 'react';
import './UserAvatar.scss';

const AVATAR_COLORS = [
  { background: '#fde2e7', color: '#b4233c' },
  { background: '#dbeafe', color: '#1e5fa8' },
  { background: '#dcfce7', color: '#17733f' },
  { background: '#fef3c7', color: '#a15c07' },
  { background: '#ede9fe', color: '#5b3cc4' },
  { background: '#cffafe', color: '#0e7490' },
];

// First letters of the first two words, e.g. "ajay k" -> "AK"
export const getNameInitials = (name?: string | null) =>
  (name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('');

// Same name, same colour, so a user keeps their colour wherever the avatar is shown
const pickColor = (name: string) => {
  let hash = 0;
  for (let index = 0; index < name.length; index += 1) hash = (hash * 31 + name.charCodeAt(index)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

// Relative avatar paths are served from Classroom's frontend URL, like the other avatar images
const resolveAvatarUrl = (src?: string | null) => {
  if (!src) return '';
  return /^(https?:|data:|blob:)/i.test(src) ? src : `${process.env.REACT_APP_FRONTEND_URL || ''}${src}`;
};

interface UserAvatarProps {
  name?: string | null;
  src?: string | null;
  size?: number;
}

/** Profile picture, or the user's initials when there is no picture or it fails to load. */
const UserAvatar: FC<UserAvatarProps> = ({ name, src, size = 40 }) => {
  const url = resolveAvatarUrl(src);
  const [hasImageError, setHasImageError] = useState(false);

  useEffect(() => setHasImageError(false), [url]);

  const sizeStyle = { width: size, height: size, fontSize: Math.round(size * 0.38) };

  if (url && !hasImageError) {
    return <img src={url} alt={name || ''} className='userAvatar' style={sizeStyle} onError={() => setHasImageError(true)} />;
  }

  const { background, color } = pickColor(name || '');
  return (
    <span className='userAvatar userAvatar--initials' style={{ ...sizeStyle, background, color }} aria-hidden='true'>
      {getNameInitials(name) || '?'}
    </span>
  );
};

export default memo(UserAvatar);
